import { Link } from 'react-router-dom';
import { ArrowUpRight, ArrowRight, Users, Zap, Camera, Instagram } from 'lucide-react';
import { SEOHead } from '../components/SEOHead';
import { ScrollReveal, StaggerContainer, StaggerItem } from '../components/ScrollReveal';
import { CTABanner } from '../components/CTABanner';

const CALENDLY_URL = 'https://calendly.com/satyam-thriveitmedia/discovery-call';
const HERO_IMG = 'https://static.prod-images.emergentagent.com/jobs/a6bba23e-9f76-4699-a363-7b1495992596/images/bc777978a5108fd73e57420b8760c3eaa9dd1a6cd2e8478bbf93f6a8c78b3b1b.png';

const services = [
  { icon: Users, title: 'Influencer Marketing', desc: 'We connect D2C brands with nano and micro influencers who drive real purchases — not just impressions. Our campaigns are built around creator-audience fit and purchase intent, not vanity metrics.', path: '/services/influencer-marketing' },
  { icon: Zap, title: 'AI UGC Ads', desc: 'High-converting ad creatives produced using AI. Looks and feels like a real brand shoot. Ready in days, not weeks. Perfect for D2C brands running Meta and Instagram ads.', path: '/services/ai-ugc-ads' },
  { icon: Camera, title: 'Real UGC Content', desc: 'Authentic content from real creators who actually use your product. Builds trust, drives engagement, and converts better than polished brand ads on paid and organic channels.', path: '/services/real-ugc' },
  { icon: Instagram, title: 'Social Media Content', desc: 'Full Instagram content pipelines for D2C brands and employer branding content for growing companies — without shoot days, without the wait, without the budget of a traditional agency.', path: '/services/social-media-content' },
];

const stats = [
  { value: '40+', label: 'Brands Worked With' },
  { value: '1500+', label: 'Creator Collaborations' },
  { value: '5x', label: 'ROAS Delivered' },
];

const testimonials = [
  { name: 'Priya Mehta', role: 'Founder, D2C Skincare Brand', text: 'We were spending a lot on shoots that took weeks to deliver. Thriveit solved that completely. We now get consistent content every week without any shoot days.' },
  { name: 'Arjun Reddy', role: 'Marketing Head, D2C Wellness Brand', text: 'The influencer campaigns they ran for us actually drove sales. Not just reach. Our ROAS on the creator content was higher than our own in-house ads.' },
  { name: 'Kavya Sharma', role: 'Founder, D2C Fashion Brand', text: 'We needed content that looked real for our Meta ads. The AI UGC they produced outperformed every polished creative we had tried before.' },
];

const steps = [
  { num: '01', title: 'Discovery Call', desc: 'We understand your brand, your goals, and what is not working right now.' },
  { num: '02', title: 'Strategy & Plan', desc: 'We build a content or campaign plan specific to your needs and budget.' },
  { num: '03', title: 'Production', desc: 'We produce AI UGC, real UGC, or influencer content — fast and on-brand.' },
  { num: '04', title: 'Launch & Optimise', desc: 'We go live, track performance, and continuously improve what works.' },
];

export default function HomePage() {
  return (
    <>
      <SEOHead
        title="Influencer Marketing & AI UGC Agency | Thriveit Media"
        description="Thriveit Media is an AI-first marketing agency. We run influencer campaigns, produce AI UGC ads, and build Instagram content for D2C brands globally."
        canonical="https://thriveitmedia.in"
      />

      {/* Hero */}
      <section data-testid="hero-section" className="relative min-h-[90vh] flex items-center overflow-hidden">
        <div className="absolute inset-0 grid-pattern" />
        <div className="absolute right-0 top-0 w-1/2 h-full hidden lg:block">
          <img src={HERO_IMG} alt="Abstract 3D geometric shape representing AI-first marketing" className="w-full h-full object-contain opacity-30" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 py-32">
          <ScrollReveal>
            <p className="text-xs md:text-sm uppercase tracking-[0.2em] font-heading font-bold text-[#FF5C35] mb-6">
              AI-First Marketing Agency — Bengaluru
            </p>
          </ScrollReveal>
          <ScrollReveal delay={0.1}>
            <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl font-extrabold tracking-tighter leading-none text-[#F5F5F0] max-w-4xl mb-8">
              Content That Looks Real. And Performs.
            </h1>
          </ScrollReveal>
          <ScrollReveal delay={0.2}>
          <p className="text-sm font-body text-[#888880] leading-relaxed mb-10">
              We don't do everything. We do four things extremely well — and each one is built to produce real results for D2C brands and ecommerce companies.
            </p>
          </ScrollReveal>
          <ScrollReveal delay={0.3}>
            <div className="flex flex-col sm:flex-row gap-4">
              <a
                href={CALENDLY_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hero-cta-book-call"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-[#FFE500] text-[#0a0a0a] font-body font-medium tracking-wide hover:bg-[#FF5C35] transition-colors duration-300 group"
              >
                Book a Free Call
                <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
              </a>
              <Link
                to="/services"
                data-testid="hero-cta-services"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 border border-white/10 text-[#F5F5F0] font-body font-medium tracking-wide hover:border-[#FF5C35] hover:text-[#FF5C35] transition-colors duration-300"
              >
                See Our Services
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </ScrollReveal>
          <ScrollReveal delay={0.4}>
            <div className="mt-16 flex flex-wrap gap-12">
              {stats.map((s) => (
                <div key={s.label} data-testid={`stat-${s.label.toLowerCase().replace(/\s/g, '-')}`}>
                  <p className="text-4xl md:text-5xl font-heading font-extrabold text-[#FFE500]">{s.value}</p>
                  <p className="text-sm font-body text-[#888880] mt-1">{s.label}</p>
                </div>
              ))}
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* Marquee */}
      <div className="border-y border-white/5 py-5 overflow-hidden bg-[#111111]">
        <div className="marquee-track">
          {[...Array(2)].map((_, i) => (
            <div key={i} className="flex items-center gap-8 mr-8 whitespace-nowrap">
              {['INFLUENCER MARKETING', 'AI UGC ADS', 'REAL UGC CONTENT', 'SOCIAL MEDIA', 'EMPLOYER BRANDING', 'D2C GROWTH', 'CONTENT PRODUCTION'].map((t) => (
                <span key={`${i}-${t}`} className="text-sm font-heading font-bold tracking-[0.15em] text-[#888880]">
                  {t} <span className="text-[#FFE500] mx-4">/</span>
                </span>
              ))}
            </div>
          ))}
        </div>
      </div>

      {/* Services */}
      <section data-testid="services-section" className="py-24 md:py-32 bg-[#0a0a0a]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <ScrollReveal>
            <p className="text-xs uppercase tracking-[0.2em] font-heading font-bold text-[#FF5C35] mb-4">What We Do</p>
            <h2 className="font-heading text-4xl md:text-5xl font-bold tracking-tight text-[#F5F5F0] mb-16">Our Services</h2>
          </ScrollReveal>
          <StaggerContainer className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {services.map((s) => (
              <StaggerItem key={s.title}>
                <Link
                  to={s.path}
                  data-testid={`service-card-${s.path.split('/').pop()}`}
                  className="block bg-[#111111] border border-white/5 p-8 md:p-12 group hover:border-[#FF5C35]/30 transition-all duration-500 hover:-translate-y-1"
                >
                  <s.icon className="w-8 h-8 text-[#FF5C35] mb-6" strokeWidth={1.5} />
                  <h3 className="font-heading text-2xl md:text-3xl font-bold tracking-tight text-[#F5F5F0] mb-4 group-hover:text-[#FF5C35] transition-colors">{s.title}</h3>
                  <p className="text-sm md:text-base font-body font-light text-[#888880] leading-relaxed mb-6">{s.desc}</p>
                  <span className="inline-flex items-center gap-2 text-sm font-body text-[#FF5C35] group-hover:gap-3 transition-all">
                    Learn More <ArrowRight className="w-4 h-4" />
                  </span>
                </Link>
              </StaggerItem>
            ))}
          </StaggerContainer>
        </div>
      </section>

      {/* Why Thriveit */}
      <section data-testid="why-section" className="py-24 md:py-32 bg-[#111111] relative">
        <div className="absolute inset-0 grid-pattern opacity-40" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <ScrollReveal>
                <p className="text-xs uppercase tracking-[0.2em] font-heading font-bold text-[#FF5C35] mb-4">Why Us</p>
                <h2 className="font-heading text-4xl md:text-5xl font-bold tracking-tight text-[#F5F5F0] mb-10">Why D2C Brands Choose Thriveit Media</h2>
              </ScrollReveal>
              <StaggerContainer className="space-y-8">
                {[
                  { title: 'No Shoot Days Needed', desc: 'Traditional shoots are expensive, slow, and often outdated before they go live. We use AI and real UGC production to deliver brand-quality content faster and at a fraction of the cost.' },
                  { title: 'Creators Who Actually Convert', desc: 'We source nano and micro influencers who have genuine buying influence with your target audience — not just large follower counts.' },
                  { title: 'AI-First Production', desc: 'We use AI tools to produce more creative variations, faster, and at scale — consistent, on-brand content every week without burning through a production budget.' },
                  { title: 'Built for D2C and Employer Brands', desc: 'We understand the D2C growth model — rising CAC, creative fatigue, and the pressure to produce more content with smaller budgets.' },
                ].map((item) => (
                  <StaggerItem key={item.title} className="flex gap-5">
                    <div className="w-1 bg-[#FFE500] flex-shrink-0" />
                    <div>
                      <h3 className="font-heading text-lg font-bold text-[#F5F5F0] mb-1">{item.title}</h3>
                      <p className="text-sm font-body font-light text-[#888880]">{item.desc}</p>
                    </div>
                  </StaggerItem>
                ))}
              </StaggerContainer>
            </div>
            <ScrollReveal delay={0.2}>
              <div className="bg-[#0a0a0a] border border-white/5 p-10">
                <p className="text-xs uppercase tracking-[0.2em] font-heading font-bold text-[#FF5C35] mb-6">Results</p>
                <div className="space-y-6">
                  {stats.map((s) => (
                    <div key={s.label} className="flex items-end justify-between border-b border-white/5 pb-4">
                      <span className="text-sm font-body text-[#888880]">{s.label}</span>
                      <span className="text-3xl font-heading font-extrabold text-[#FFE500]">{s.value}</span>
                    </div>
                  ))}
                </div>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* How We Work */}
      <section data-testid="how-section" className="py-24 md:py-32 bg-[#0a0a0a]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <ScrollReveal>
            <p className="text-xs uppercase tracking-[0.2em] font-heading font-bold text-[#FF5C35] mb-4">Process</p>
            <h2 className="font-heading text-4xl md:text-5xl font-bold tracking-tight text-[#F5F5F0] mb-16">How We Work</h2>
          </ScrollReveal>
          <StaggerContainer className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {steps.map((step) => (
              <StaggerItem key={step.num}>
                <div data-testid={`step-${step.num}`} className="bg-[#111111] border border-white/5 p-8 group hover:border-[#FF5C35]/30 transition-all duration-500">
                  <span className="text-5xl font-heading font-extrabold text-[#FFE500]/10 group-hover:text-[#FFE500]/30 transition-colors">{step.num}</span>
                  <h3 className="font-heading text-lg font-bold text-[#F5F5F0] mt-4 mb-2">{step.title}</h3>
                  <p className="text-sm font-body font-light text-[#888880] leading-relaxed">{step.desc}</p>
                </div>
              </StaggerItem>
            ))}
          </StaggerContainer>
        </div>
      </section>

      {/* Testimonials */}
      <section data-testid="testimonials-section" className="py-24 md:py-32 bg-[#111111]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <ScrollReveal>
            <p className="text-xs uppercase tracking-[0.2em] font-heading font-bold text-[#FF5C35] mb-4">Testimonials</p>
            <h2 className="font-heading text-4xl md:text-5xl font-bold tracking-tight text-[#F5F5F0] mb-16">What Our Clients Say</h2>
          </ScrollReveal>
          <StaggerContainer className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {testimonials.map((t) => (
              <StaggerItem key={t.name}>
                <div data-testid={`testimonial-${t.name.toLowerCase().replace(/\s/g, '-')}`} className="bg-[#0a0a0a] border border-white/5 p-8 flex flex-col h-full">
                  <p className="text-sm md:text-base font-body font-light text-[#888880] leading-relaxed mb-8 flex-1">"{t.text}"</p>
                  <div>
                    <p className="font-heading text-sm font-bold text-[#F5F5F0]">{t.name}</p>
                    <p className="text-xs font-body text-[#FF5C35]">{t.role}</p>
                  </div>
                </div>
              </StaggerItem>
            ))}
          </StaggerContainer>
        </div>
      </section>

      <CTABanner />
    </>
  );
}
